import { defineStore } from 'pinia';
import { myLogin as login, myRegister as register, myGetCurrentUser as getCurrentUser, myLogout as logout } from '@/api/myUserApi';
import { message } from 'ant-design-vue';
import tokenService from '@/utils/tokenService';
import permissionManager from '@/utils/permissionManager';
import router from '@/router';
import userCacheService from '@/services/UserCacheService';
import { useModuleStore } from '@/store/module';
import { usePlaybackStore } from '@/store/playback';
import {
  USER_ROLE_ADMIN,
  USER_ROLE_TEST,
  hasDevToolsAccess
} from '@/constants/userRole';

// 判断当前是否在音乐业务路由下
const isMusicBusinessRoute = () => {
  const currentRoute = router.currentRoute.value;
  // 音乐业务相关路由：/imusic, /music-auth, /music-auth-demo 等
  const musicRoutes = ['/imusic', '/music-auth', '/music-auth-demo'];
  return musicRoutes.some(route => currentRoute.path.includes(route));
};

export const useUserStore = defineStore('user', {
  state: () => ({
    // 当前用户信息
    currentUser: null,
    // 登录状态，通过检查访问令牌判断
    isLoggedIn: tokenService.hasAccessToken(),
    // 加载状态
    loading: false
  }),
  
  getters: {
    // 获取用户角色
    userRole: (state) => state.currentUser?.userRole || 0,
    // 是否是管理员
    isAdmin: (state) => state.currentUser?.userRole === USER_ROLE_ADMIN,
    // 是否是测试账号
    isTestAccount: (state) => state.currentUser?.userRole === USER_ROLE_TEST,
    // 是否可使用开发者工具（开发模式、元素选择、Eruda）
    hasDevTools: (state) => hasDevToolsAccess(state.currentUser?.userRole),
    // 获取用户名
    username: (state) => state.currentUser?.username || state.currentUser?.userAccount || ''
  },
  
  actions: {
    /**
     * 用户登录
     * @param {Object} loginData - 登录信息
     * @returns {Promise<Object>}
     */
    async userLogin(loginData) {
      this.loading = true;
      try {
        console.log('开始登录操作...');
        const userData = await login(loginData);
        console.log('登录API调用成功，返回的用户数据:', userData);
        
        // 检查userData是否存在以及是否包含关键字段
        if (!userData) {
          console.error('登录API返回的用户数据为空');
          throw new Error('登录成功但未获取到用户信息');
        }
        
        // 保存令牌
        if (userData.accessToken) {
          tokenService.saveAccessToken(userData.accessToken);
        }
        if (userData.refreshToken) {
          tokenService.saveRefreshToken(userData.refreshToken);
        }
        
        // 登录成功后更新用户信息和登录状态
        this.currentUser = userData;
        this.isLoggedIn = true;

        // 同步权限状态
        permissionManager.refreshPermissionState();

        const moduleStore = useModuleStore();
        await moduleStore.fetchModules(true);

        // 初始化用户音乐缓存
        await this.initializeUserCache(userData);

        // 注意：不再使用localStorage存储登录状态，完全依赖JWT Token
        
        // 立即验证localStorage是否已更新
        console.log('验证localStorage更新状态:', {
          isLoggedIn: localStorage.getItem('isLoggedIn'),
          userRole: localStorage.getItem('userRole')
        });
        
        // 显示登录成功提示（音乐业务下不显示弹窗）
        if (!isMusicBusinessRoute()) {
          message.success('登录成功');
        }
        console.log('用户状态已更新：', {
          isLoggedIn: this.isLoggedIn,
          currentUser: this.currentUser,
          userRole: this.userRole,
          isAdmin: this.isAdmin
        });
        
        return userData;
      } catch (error) {
        // 登录失败，不更新状态
        console.error('登录操作失败:', error);
        
        // 特殊处理：如果错误消息是"登录成功"，说明后端成功了但前端有问题
        if (error.message && error.message.includes('登录成功')) {
          console.log('检测到特殊情况：成功响应被错误处理');
          // 尝试重新获取用户信息
          try {
            const userData = await this.fetchCurrentUser();
            console.log('通过获取当前用户信息成功恢复登录状态');
            if (!isMusicBusinessRoute()) {
              message.success('登录成功');
            }
            return userData;
          } catch (innerError) {
            console.error('尝试恢复登录状态失败:', innerError);
          }
        }
        
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 初始化用户音乐缓存
     * @param {Object} userData - 用户数据
     */
    async initializeUserCache(userData) {
      try {
        console.log('👤 [用户Store] 开始初始化用户音乐缓存...');
        const cacheInitResult = await userCacheService.initializeUserCache(userData);
        
        if (cacheInitResult.validation && cacheInitResult.validation.cleanupResults) {
          const cleaned = cacheInitResult.validation.cleanupResults.cleaned;
          if (cleaned > 0) {
            console.log(`👤 [用户Store] 缓存初始化完成，清理了 ${cleaned} 个无效缓存`);
          } else {
            console.log('👤 [用户Store] 缓存初始化完成，无需清理');
          }
        }
        
        return cacheInitResult;
      } catch (error) {
        console.error('👤 [用户Store] 用户缓存初始化失败:', error);
        // 缓存初始化失败不应该影响用户登录，只记录错误
      }
    },
    
    /**
     * 用户注册
     * @param {Object} registerData - 注册信息
     * @returns {Promise<number>}
     */
    async userRegister(registerData) {
      this.loading = true;
      try {
        const userId = await register(registerData);
        // 显示注册成功提示
        message.success('注册成功');
        return userId;
      } catch (error) {
        console.error('注册失败:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * 获取当前用户信息
     * @returns {Promise<Object>}
     */
    async fetchCurrentUser() {
      this.loading = true;
      try {
        // 检查是否有访问令牌
        if (!tokenService.hasAccessToken()) {
          console.info('未登录状态，以游客身份浏览');
          this.currentUser = null;
          this.isLoggedIn = false;
          
          // 注意：不再使用localStorage存储登录状态
          
          return null;
        }
        
        const userData = await getCurrentUser();
        
        // 处理未登录情况
        if (!userData) {
          console.info('未登录状态，以游客身份浏览');
          this.currentUser = null;
          this.isLoggedIn = false;
          
          // 注意：不再使用localStorage存储登录状态

          tokenService.clearTokens();
          permissionManager.refreshPermissionState();
          return null;
        }
        
        console.log('获取到当前用户信息:', userData);
        
        // 更新用户信息和登录状态
        this.currentUser = userData;
        this.isLoggedIn = true;

        // 同步权限状态
        permissionManager.refreshPermissionState();

        const moduleStore = useModuleStore();
        await moduleStore.fetchModules(true);

        // 初始化用户音乐缓存
        await this.initializeUserCache(userData);

        // 注意：不再使用localStorage存储登录状态，完全依赖JWT Token
        
        console.log('用户状态已更新（来自current）:', {
          isAdmin: this.isAdmin,
          userRole: this.userRole
        });
        
        return userData;
      } catch (error) {
        // 处理其他错误
        console.error('获取当前用户信息失败:', error);
        this.currentUser = null;
        this.isLoggedIn = false;
        
        // 注意：不再使用localStorage存储登录状态

        tokenService.clearTokens();
        permissionManager.refreshPermissionState();
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * 用户登出
     * @returns {Promise<void>}
     */
    async userLogout() {
      this.loading = true;
      try {
        await logout();
        // 清除用户信息和登录状态
        this.currentUser = null;
        this.isLoggedIn = false;

        // 清除令牌
        tokenService.clearTokens();

        const moduleStore = useModuleStore();
        moduleStore.reset();

        // 清除localStorage中的身份信息
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userRole');
        localStorage.removeItem('returnUrl');
        
        // 清除sessionStorage中的身份信息
        sessionStorage.removeItem('isLoggedIn');
        sessionStorage.removeItem('userRole');
        
        // 🔥 清除播放状态（退出登录时清除上一个账号的播放状态）
        try {
          const playbackStore = usePlaybackStore();
          playbackStore.reset();
          console.log('🎵 [登出] 已清除播放状态');
        } catch (error) {
          console.warn('⚠️ [登出] 清除播放状态失败（不影响退出登录）:', error);
        }
        
        // 调试：检查清理后的localStorage状态
        console.log('🔐 [登出] 已清理所有身份信息和令牌');
        console.log('🔍 [调试] 清理后的localStorage状态:', {
          accessToken: localStorage.getItem('accessToken'),
          refreshToken: localStorage.getItem('refreshToken'),
          loginTimestamp: localStorage.getItem('loginTimestamp'),
          isLoggedIn: localStorage.getItem('isLoggedIn'),
          userRole: localStorage.getItem('userRole')
        });

        // 同步权限状态
        permissionManager.refreshPermissionState();
        
        // 显示登出成功提示（音乐业务下不显示弹窗）
        if (!isMusicBusinessRoute()) {
          message.success('已安全退出登录');
        }
      } catch (error) {
        console.error('登出失败:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    }
  }
});